import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";

import { downloadAndSaveFile } from "common/downloadFile";
import { getTabToken } from "common/MetaActions";
import { Icon } from "components/icon/Icon";
import { Loader } from "components/loader/Loader";
import config from "config/config";
import { Attachment } from "models/dashboard/dashboard";
import { DocumentUploadFile } from "models/DocumentUploadFile";
import ThreadMessageList from "./threadMessageList/ThreadMessageList";
import { useRootStore } from "store";
import FileUploadErrorModal from "components/shared/fileUploadErrorModal/FileUploadErrorModal";
import useSetErrorOnUpload from "hooks/use-set-error-on-upload";

import "./Thread.scss";

export default function Thread() {
  const navigate = useNavigate();

  const [missingCredentials, setMissingCredentials] = useState(false);

  const {
    currentThread,
    loadingThread,
    isLoadingThread,
    deleteThreadMessage,
    fetchThread,
    sendMessage,
    failedToGetThread
  } = useRootStore((state) => state.inbox);

  const { workingCustomer: customer } = useRootStore(
    (state) => state.customers
  );

  const { user, archive } = useRootStore((state) => state);
  const { threadId } = useParams<{ threadId: string }>();

  const { customerNumber: customerId, isLoading: isLoadingCustomer } = customer;
  const { sendFileToArchive } = archive;

  const { failed, setFailed, failMessage, setFailedWrapper } =
    useSetErrorOnUpload();

  useEffect(() => {
    if (threadId && customerId) {
      fetchThread(customerId, threadId);
    }
  }, [threadId, customerId, fetchThread]);

  if (!customerId && !isLoadingCustomer) {
    return <></>;
  }

  const handleDeleteMessage = (messageId: string, messageType: string) => {
    if (currentThread) {
      deleteThreadMessage({
        customerId: customerId,
        threadId: currentThread.threadId,
        messageId,
        messageType,
        navigate
      });
    }
  };

  const handleOnReply = async (
    body: string,
    users: string[],
    replayThreadId: string,
    attachments: Attachment[],
    filesToUpload: DocumentUploadFile[]
  ) => {
    try {
      loadingThread();

      const result = await sendFileToArchive(
        filesToUpload,
        customerId,
        "Inbox",
        setFailedWrapper
      );

      if (!Array.isArray(result)) {
        if (threadId && customerId) {
          fetchThread(customerId, threadId);
        }
        return;
      }

      const uploadedAttachments = result.map((file) => ({
        id: file.id,
        name: file.name,
        type: file.type
      }));

      const allAttachments = [...attachments, ...uploadedAttachments];
      sendMessage(
        user.userInfo ? user.userInfo.id : "",
        body,
        "",
        replayThreadId,
        users,
        customerId,
        allAttachments
      );
    } catch (error) {
      console.error("Error while replying:", error);
      failedToGetThread();
    }
  };

  const handleOpenDocument = (item: { id: string; name: string }) => {
    const url = config.api.archive.download(customerId, item.id);
    const headers = new Headers();
    headers.append("Authorization", `Bearer ${getTabToken()}`);
    downloadAndSaveFile(url, item.name, headers);
  };

  const oncloseTriggered = () => {
    setMissingCredentials(false);
  };

  return (
    <div>
      {failed && (
        <FileUploadErrorModal failMessage={failMessage} setFailed={setFailed} />
      )}
      {missingCredentials && (
        <div className="missingCredentials">
          <span>Du saknar rättigheter att öppna denna fil.</span>
          <Icon name="close" onClick={oncloseTriggered} title="Stäng" />
        </div>
      )}

      <ThreadMessageList
        onReply={handleOnReply}
        onDeleteMessage={handleDeleteMessage}
        onOpenDocument={handleOpenDocument}
      />

      {(isLoadingThread || isLoadingCustomer) && (
        <div className={"d-flex align-items-center justify-content-center"}>
          <Loader className={"mr-3"} /> Laddar konversationen...
        </div>
      )}

      {!isLoadingThread && !currentThread && !isLoadingCustomer && (
        <div>
          Vi kunde inte ladda konversationen! Kontakta support för att få hjälp.
        </div>
      )}
    </div>
  );
}
